import {
  AdminInvoicesResponse,
  AdminOrganisationResponse,
  AdminTeamResponse,
  Invitation,
  Invoice,
  Organisation,
  UserWithRole,
} from '@avanzu/types';
import AssignmentTwoTone from '@mui/icons-material/AssignmentTwoTone';
import BusinessTwoTone from '@mui/icons-material/BusinessTwoTone';
import CancelTwoTone from '@mui/icons-material/CancelTwoTone';
import CheckCircleTwoTone from '@mui/icons-material/CheckCircleTwoTone';
import CloudSyncTwoTone from '@mui/icons-material/CloudSyncTwoTone';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import GroupAddTwoTone from '@mui/icons-material/GroupAddTwoTone';
import GroupTwoTone from '@mui/icons-material/GroupTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddTwoTone from '@mui/icons-material/PlaylistAddTwoTone';
import RequestQuoteTwoTone from '@mui/icons-material/RequestQuoteTwoTone';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ActivityList from '../../../components/ActivityList';
import BreadCrumb from '../../../components/BreadCrumb';
import UserChip from '../../../components/UserChip';
import { getMonthName } from '../../../helpers/getMonthName';
import getRoleColor from '../../../helpers/getRoleColor';
import getRoleLabel from '../../../helpers/getRoleLabel';
import getStatusColor from '../../../helpers/getStatusColor';
import { apiClient } from '../../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../../utils/formatDate';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';
import OrganisationQuotaEditor from '../components/OrganisationQuotaEditor';
import PaperSection from '../../../components/PaperSection';

export default function OrganisationView() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalTransactionsAllocated, setTotalTransactionsAllocated] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (organisationId) {
      fetchOrganisation();
      fetchInvoices();
      fetchTeam();
    }
  }, [organisationId]);

  const fetchOrganisation = async () => {
    if (!organisationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/organisations/${organisationId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch organisation details');
      }

      const data: AdminOrganisationResponse = await response.json();
      setOrganisation(data.organisation);
      setTotalProjects(data.totalProjects);
      setTotalTransactions(data.totalTransactions);
      setTotalTransactionsAllocated(data.totalTransactionsAllocated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching organisation:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    if (!organisationId) return;

    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const response = await apiClient.get(
        `/admin/invoices/organisation/${organisationId}?limit=10`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data: AdminInvoicesResponse = await response.json();
      setInvoices(data.invoices);
    } catch (err) {
      setInvoicesError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchTeam = async () => {
    if (!organisationId) return;

    setTeamLoading(true);
    setTeamError(null);
    try {
      const response = await apiClient.get(`/admin/team/organisation/${organisationId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch team');
      }

      const data: AdminTeamResponse = await response.json();
      setTeamMembers(data.users || []);
      setInvitations(data.invitations || []);
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching team:', err);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleDeleteOrganisation = async () => {
    if (confirmText !== 'DELETE') {
      setDeleteError('Please type "DELETE" to confirm');
      return;
    }

    if (!organisationId) {
      setDeleteError('Organisation ID is missing');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await apiClient.delete(`/admin/organisations/${organisationId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete organisation');
      }

      navigate('/app/organisations');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'An error occurred while deleting');
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setConfirmText('');
    setDeleteError(null);
  };

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const StatCard = ({
    icon,
    label,
    value,
    color = 'primary.main',
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    color?: string;
  }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !organisation) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <BreadCrumb />
          <Typography variant="h5" color="error">
            {error || 'Organisation not found'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <BreadCrumb customLabel={organisation.name} />

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
            <BusinessTwoTone sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {organisation.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created {formatDate(organisation.createdAt, DateFormatOptions.DATEONLY)}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={organisation.subscriptionStatus}
            color={getSubscriptionStatusColor(organisation.subscriptionStatus)}
            sx={{ height: 32, fontSize: '0.875rem' }}
          />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={openDeleteDialog}
            disabled={isDeleting}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={invoicesLoading ? <CircularProgress size={24} /> : <RequestQuoteTwoTone />}
            label="Lifetime Value"
            value={`${invoices.reduce((sum, inv) => sum + inv.amountDue, 0).toFixed(2)} ${invoices[0]?.currency.toUpperCase() || 'AUD'}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<GroupTwoTone />}
            label="Team Members"
            value={`${teamMembers.length} / ${organisation.quotaMaxUsers === -1 ? 'Unlimited' : organisation.quotaMaxUsers}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AssignmentTwoTone />} label="Total Projects" value={totalProjects} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PlaylistAddTwoTone />}
            label="Transactions"
            value={`${totalTransactionsAllocated} / ${totalTransactions}`}
          />
        </Grid>
      </Grid>

      {/* Main Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={4}>
            {/* Organization & Contact */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Organization & Contact
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Legal Name" value={organisation.legalName} />
                <InfoItem label="ABN" value={organisation.abn} />
                <InfoItem label="Contact Number" value={organisation.contactNumber} />
                <InfoItem label="Website" value={organisation.website} />
              </Stack>
            </Grid>

            {/* Subscription & Billing */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Tier & Billing
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Billing Email" value={organisation.billingEmail} />
                <InfoItem
                  label="Current Tier"
                  value={organisation.currentTierProductId || 'free'}
                />
                <InfoItem
                  label="Free Trial Used"
                  value={organisation.hasUsedFreeTier ? 'Yes' : 'No'}
                />
                <InfoItem
                  label="Card Expires"
                  value={formatDate(organisation.cardValidUntil, DateFormatOptions.DATEONLY)}
                />
                <InfoItem
                  label="Stripe Customer ID"
                  value={
                    organisation.stripeCustomerId ? (
                      <Link
                        href={`https://dashboard.stripe.com/customers/${organisation.stripeCustomerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {organisation.stripeCustomerId}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      'N/A'
                    )
                  }
                />
                {organisation.promotionCode && (
                  <InfoItem
                    label="Promotion Code Used"
                    value={
                      <Chip
                        label={organisation.promotionCode}
                        color="primary"
                        size="small"
                        sx={{ fontFamily: 'monospace' }}
                        onClick={() =>
                          organisation.couponId && navigate(`/app/coupons/${organisation.couponId}`)
                        }
                      />
                    }
                  />
                )}
              </Stack>
            </Grid>

            {/* Integration & Setup */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Integration & Setup
              </Typography>
              <Stack spacing={2}>
                <InfoItem
                  label="Xero Connection"
                  value={
                    <Chip
                      label={organisation.connectedToXero ? 'Connected' : 'Not Connected'}
                      color={organisation.connectedToXero ? 'success' : 'default'}
                      size="small"
                    />
                  }
                />
                <InfoItem
                  label="Last Xero Sync"
                  value={formatDate(
                    organisation.xeroLastSyncTransactionsAt,
                    DateFormatOptions.DATEANDTIME
                  )}
                />
                <InfoItem
                  label="Tax Year Start"
                  value={getMonthName(organisation.taxYearStartMonth)}
                />
                <InfoItem
                  label="Onboarding"
                  value={
                    <Chip
                      label={organisation.completedOnboarding ? 'Complete' : 'Incomplete'}
                      color={organisation.completedOnboarding ? 'success' : 'warning'}
                      size="small"
                    />
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Onboarding Checklist */}
      <PaperSection title="Onboarding Checklist">
        <Grid container spacing={2}>
          {[
            {
              label: 'Connected to Xero',
              icon: <CloudSyncTwoTone />,
              completed: organisation.connectedToXero,
            },
            {
              label: 'Created Project',
              icon: <AssignmentTwoTone />,
              completed: organisation.createdProject,
            },
            {
              label: 'Mapped First Transaction',
              icon: <PlaylistAddTwoTone />,
              completed: organisation.mappedFirstTransaction,
            },
            {
              label: 'Invited Teammates',
              icon: <GroupAddTwoTone />,
              completed: organisation.invitedTeammates,
            },
          ].map((step) => (
            <Grid item xs={12} sm={6} md={3} key={step.label}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ color: step.completed ? 'success.main' : 'text.disabled', display: 'flex' }}>
                      {step.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {step.label}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        {step.completed ? (
                          <CheckCircleTwoTone color="success" fontSize="small" />
                        ) : (
                          <CancelTwoTone color="disabled" fontSize="small" />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {step.completed ? 'Complete' : 'Incomplete'}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </PaperSection>

      {/* Organisation Quotas */}
      <OrganisationQuotaEditor organisation={organisation} onUpdate={setOrganisation} />

      {/* Users card */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Team (${teamMembers.length})`} />
          <Tab label={`Invitations (${invitations.length})`} />
        </Tabs>

        {/* Team Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            {teamLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : teamError ? (
              <Typography color="error">{teamError}</Typography>
            ) : teamMembers.length === 0 ? (
              <Typography color="text.secondary">No active team members</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow
                        key={member.id}
                        onClick={() => navigate(`/app/users/${member.id}`)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer',
                          },
                        }}
                      >
                        <TableCell>
                          <UserChip user={member} clickable={false} />
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(member.role)}
                            size="small"
                            color={getRoleColor(member.role)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.status}
                            size="small"
                            sx={{ backgroundColor: getStatusColor(member.status) }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(member.joinedAt, DateFormatOptions.DATEONLY)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Invitations Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            {teamLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : teamError ? (
              <Typography color="error">{teamError}</Typography>
            ) : invitations.length === 0 ? (
              <Typography color="text.secondary">No pending invitations</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Invited By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell>{invitation.email}</TableCell>
                        <TableCell>
                          <Chip label={getRoleLabel(invitation.role)} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Chip label={invitation.status} size="small" color="warning" />
                        </TableCell>
                        <TableCell>
                          {formatDate(invitation.expiresAt, DateFormatOptions.DATEONLY)}
                        </TableCell>
                        <TableCell>{invitation.invitedBy || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Card>

      {/* Invoices Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Invoices
          </Typography>
          {invoicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : invoicesError ? (
            <Typography color="error">{invoicesError}</Typography>
          ) : invoices.length === 0 ? (
            <Typography color="text.secondary">No invoices found</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>PDF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => {
                    const getStatusLabel = () => {
                      if (invoice.refunded && invoice.amountRefunded && invoice.total) {
                        if (invoice.amountRefunded >= invoice.total) {
                          return 'Refunded';
                        } else {
                          return 'Partially Refunded';
                        }
                      }
                      return invoice.status;
                    };

                    const getStatusStyles = () => {
                      if (invoice.refunded && invoice.amountRefunded && invoice.total) {
                        return {
                          backgroundColor:
                            invoice.amountRefunded >= invoice.total ? '#ffebee' : '#fff3e0',
                          color: invoice.amountRefunded >= invoice.total ? '#c62828' : '#e65100',
                        };
                      }
                      return {};
                    };

                    const getStatusColor = () => {
                      if (invoice.refunded) {
                        return undefined;
                      }
                      return invoice.status === 'paid'
                        ? 'success'
                        : invoice.status === 'open'
                          ? 'warning'
                          : 'default';
                    };

                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <Link
                            href={`https://dashboard.stripe.com/invoices/${invoice.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            {invoice.number || invoice.id}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel()}
                            color={getStatusColor()}
                            size="small"
                            sx={getStatusStyles()}
                          />
                        </TableCell>
                        <TableCell>
                          {invoice.amountDue.toFixed(2)} {invoice.currency.toUpperCase()}
                        </TableCell>
                        <TableCell>
                          {formatDate(invoice.created, DateFormatOptions.DATEONLY)}
                        </TableCell>
                        <TableCell>
                          {invoice.invoicePdf ? (
                            <Link
                              href={invoice.invoicePdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              Download
                              <DownloadIcon sx={{ fontSize: 14 }} />
                            </Link>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Activities Card */}
      {organisationId && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Recent Activities
            </Typography>
            <ActivityList
              endpoint={`/admin/activities/organisation/${organisationId}`}
              showUsers={true}
              showOrganisations={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Organisation</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              This action will permanently delete <strong>{organisation.name}</strong> and all
              associated data, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              <li>All team members ({teamMembers.length} users)</li>
              <li>All projects ({totalProjects} projects)</li>
              <li>All transactions ({totalTransactions} transactions)</li>
              <li>All invitations and notifications</li>
              <li>All associated user accounts that only belong to this organisation</li>
            </Box>
            <Alert severity="warning">
              This action cannot be undone. Please be certain before proceeding.
            </Alert>
            <TextField
              fullWidth
              name="deleteConfirmation"
              id="deleteConfirmation"
              label='Type "DELETE" to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isDeleting}
            />
            {deleteError && (
              <Alert severity="error" variant="filled">
                {deleteError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrganisation}
            color="error"
            variant="contained"
            disabled={confirmText !== 'DELETE' || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Organisation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
